import { Link } from "react-router-dom";
import { useState } from "react";
import { Star, Sparkles } from "lucide-react";
import { useApp } from "../context/AppContext";
import { tr } from "../i18n/translations";
import ReviewsScroll from "./ReviewsScroll";
import SessionAssistant from "./SessionAssistant";

export default function Hero({ reviews }) {
  const { lang } = useApp();
  const [saOpen, setSaOpen] = useState(false);
  return (
    <section className="max-w-[1400px] mx-auto px-6 lg:px-12 py-16 lg:py-24 grid lg:grid-cols-[1.5fr_1fr] gap-12 lg:gap-20 items-center">
      <div>
        <p className="text-xs uppercase tracking-[0.25em] text-ink/60 mb-6" data-testid="hero-eyebrow">
          {tr(lang, "hero_eyebrow")}
        </p>
        <h1 className="font-display font-light text-5xl md:text-6xl lg:text-7xl xl:text-[88px] leading-[0.95] tracking-tight">
          {tr(lang, "hero_title_1")}
          <span className="italic text-pedablue">{tr(lang, "hero_title_em")}</span>
          {tr(lang, "hero_title_2")}
        </h1>
        <p className="mt-8 text-lg text-ink-soft max-w-xl leading-relaxed" data-testid="hero-sub">
          Ressources PDF, applications et matériel pour orthophonistes — tout ce qu&apos;il faut pour préparer vos séances plus rapidement.
        </p>

        {/* Assistant de séance — CTA principal Hero */}
        <button
          type="button"
          onClick={() => setSaOpen(true)}
          data-testid="hero-cta-session-assistant"
          className="mt-8 group relative inline-flex items-center gap-3 rounded-full bg-gradient-to-r from-pedablue via-pedablue-dark to-coral-dark text-ivory px-6 py-4 text-base font-medium shadow-lg hover:shadow-xl hover:-translate-y-0.5 transition-all"
        >
          <span className="w-9 h-9 rounded-full bg-ivory/20 flex items-center justify-center shrink-0 group-hover:rotate-12 transition-transform">
            <Sparkles size={17} />
          </span>
          <span className="flex flex-col items-start leading-tight">
            <span className="text-[10px] uppercase tracking-widest opacity-80">Nouveau</span>
            <span>Préparez votre séance en 30 secondes</span>
          </span>
          <span className="text-xl group-hover:translate-x-1 transition-transform">→</span>
        </button>

        <div className="mt-6 flex flex-wrap gap-3">
          <Link to="/ressources-a-telecharger" className="btn-outline" data-testid="hero-cta-resources">
            Découvrir les ressources
          </Link>
          <Link to="/logiciels" className="btn-outline" data-testid="hero-cta-apps">
            Voir les applications
          </Link>
          <Link to="/catalogue" className="btn-outline" data-testid="hero-cta-material">
            Voir le matériel
          </Link>
        </div>

        <div className="mt-12 flex items-center gap-4">
          <div className="flex -space-x-2">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className={`w-8 h-8 rounded-full border-2 border-ivory ${["bg-pedablue", "bg-sage", "bg-coral", "bg-ink"][i - 1]}`}></div>
            ))}
          </div>
          <div className="text-sm">
            <div className="flex items-center gap-1">
              {[1, 2, 3, 4, 5].map((i) => <Star key={i} size={14} className="fill-coral text-coral" />)}
              <span className="ms-2 font-medium">4.9/5</span>
            </div>
            <p className="text-ink-soft text-xs mt-0.5">5 000+ orthophonistes & enseignants</p>
          </div>
        </div>
      </div>

      <div className="relative">
        <div className="absolute -top-4 -end-2 sticker-coral z-10">Avis vérifiés</div>
        <div className="bg-white rounded-3xl border border-ink/10 p-6 h-[520px] overflow-hidden relative" data-testid="hero-reviews-block">
          <div className="flex items-center justify-between mb-5">
            <div>
              <div className="flex items-center gap-1 mb-1">
                {[1, 2, 3, 4, 5].map((i) => <Star key={i} size={16} className="fill-coral text-coral" />)}
              </div>
              <p className="font-display text-2xl">4.9 / 5</p>
              <p className="text-xs text-ink-soft uppercase tracking-widest mt-1">Note moyenne</p>
            </div>
            <div className="text-end">
              <p className="text-3xl font-display text-pedablue">{reviews?.length || 8}+</p>
              <p className="text-xs text-ink-soft">avis pros</p>
            </div>
          </div>
          <div className="h-[400px] overflow-hidden relative">
            <ReviewsScroll reviews={reviews || []} />
            <div className="absolute inset-x-0 top-0 h-12 bg-gradient-to-b from-white to-transparent pointer-events-none"></div>
            <div className="absolute inset-x-0 bottom-0 h-12 bg-gradient-to-t from-white to-transparent pointer-events-none"></div>
          </div>
        </div>
      </div>
      {saOpen && <SessionAssistant onClose={() => setSaOpen(false)} />}
    </section>
  );
}
